import { FC } from 'react';
import * as ScrollArea from '@radix-ui/react-scroll-area';

import type { CourseOption } from '@teachur/http-client';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';
import { CourseBuilderItem } from './CourseBuilderItem';

interface CourseBuilderListProps {
  courses: CourseOption[];
  selectedIds: Set<number>;
  excludedIds: Set<number>;
  headroom: number;
  countsTowardLabel: string;
  selectable: boolean;
  onToggle: (courseId: number) => void;
}

export const CourseBuilderList: FC<CourseBuilderListProps> = ({
  courses,
  selectedIds,
  excludedIds,
  headroom,
  countsTowardLabel,
  selectable,
  onToggle,
}) => {
  if (courses.length === 0) {
    return <EmptyState message="No courses match your search." />;
  }

  const items = courses.map((course) => {
    const selected = selectedIds.has(course.courseId);
    const excluded = excludedIds.has(course.courseId);
    const overLimit = !selected && course.credits > headroom;
    return (
      <CourseBuilderItem
        key={course.courseId}
        course={course}
        isSelected={selected}
        disabled={excluded || overLimit}
        disabledReason={excluded ? 'Already in your plan' : overLimit ? 'No credits left' : null}
        countsTowardLabel={countsTowardLabel}
        selectable={selectable}
        onToggle={onToggle}
      />
    );
  });

  return (
    <ScrollArea.Root className="min-h-0 flex-1 overflow-hidden">
      <ScrollArea.Viewport className="h-full w-full">
        <div className="flex flex-col gap-3 pr-3">{items}</div>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        orientation="vertical"
        className="flex w-2.5 bg-transparent p-0.5 transition-colors hover:bg-gray-100"
      >
        <ScrollArea.Thumb className="flex-1 rounded-full bg-gray-300 transition-colors hover:bg-gray-400" />
      </ScrollArea.Scrollbar>
    </ScrollArea.Root>
  );
};
